import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './layout';
import Login from './login';
import { ByteBuddiesSocket, User } from '../types';
import UserContext from '../contexts/UserContext';
import QuestionPage from './main/questionPage';
import TagPage from './main/tagPage';
import NewQuestionPage from './main/newQuestion';
import NewAnswerPage from './main/newAnswer';
import AnswerPage from './main/answerPage';
import NotificationsPage from './main/notificationsPage';
import SearchUsersPage from './main/searchUsersPage';
import ProfilePage from './main/profilePage';
import CreateAccount from './createAccount';
import ExplorePage from './main/explorePage';
import useByteBuddies from '../hooks/useByteBuddies';
import LoadingScreen from './loadingScreen';

const ProtectedRoute = ({
  user,
  socket,
  children,
}: {
  user: User | null;
  socket: ByteBuddiesSocket | null;
  children: JSX.Element;
}) => {
  if (!user || !socket) {
    socket?.disconnect();
    return <Navigate to='/' replace />;
  }

  return <UserContext.Provider value={{ user, socket }}>{children}</UserContext.Provider>;
};

/**
 * Represents the main component of the application.
 * It manages the state for search terms and the main title.
 */
const ByteBuddies = ({ socket }: { socket: ByteBuddiesSocket | null }) => {
  const { user, isCheckingAuth } = useByteBuddies(socket);

  if (isCheckingAuth) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      {/* Public Route */}
      <Route path='/' element={<Login />} />
      <Route path='/create-account' element={<CreateAccount />} />

      {/* Protected Routes */}
      {
        <Route
          element={
            <ProtectedRoute user={user} socket={socket}>
              <Layout />
            </ProtectedRoute>
          }>
          <Route path='/home' element={<ExplorePage />} />
          <Route path='/questions' element={<QuestionPage />} />
          <Route path='/tags' element={<TagPage />} />
          <Route path='/question/:qid' element={<AnswerPage />} />
          <Route path='/new/question' element={<NewQuestionPage />} />
          <Route path='/new/answer/:qid' element={<NewAnswerPage />} />
          <Route path='/notifications' element={<NotificationsPage />} />
          <Route path='/search-users' element={<SearchUsersPage />} />
          <Route path='/profile/:username' element={<ProfilePage />} />
        </Route>
      }
    </Routes>
  );
};

export default ByteBuddies;
